import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { getUserPermissions } from "src/utils/permissionsUtils";

const PASSCODE_PATTERN = /^\d{6}$/;
const DEFAULT_SETTINGS = { enabled: false, passcode: "" };

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export default function EditPassCodeSettingsDialog({
  open,
  onClose,
  rawSettings,
  isSaving = false,
  onSave,
}) {
  const { isViewer } = getUserPermissions();
  const [enabled, setEnabled] = useState(false);
  const [passcode, setPasscode] = useState("");

  useEffect(() => {
    if (!open) return;

    const parsed = tryParseJson(rawSettings);
    const settings =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : DEFAULT_SETTINGS;

    setEnabled(settings.enabled === true);
    setPasscode(typeof settings.passcode === "string" ? settings.passcode : "");
  }, [open, rawSettings]);

  const controlsDisabled = isViewer || isSaving;
  const isPasscodeValid = passcode === "" || PASSCODE_PATTERN.test(passcode);
  const canSave = isPasscodeValid && (!enabled || PASSCODE_PATTERN.test(passcode));

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handlePasscodeChange = (event) => {
    setPasscode(event.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave(JSON.stringify({ enabled, passcode }, null, 2));
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Passcode Settings</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={controlsDisabled}
                  color={enabled ? "success" : "primary"}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {enabled ? "Enabled" : "Disabled"}
                </Typography>
              }
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", ml: 1 }}
            >
              Controls the splash passcode screen shown when the app opens.
              When enabled, users must enter this 6-digit code before they can
              continue. When disabled, that screen is not shown.
            </Typography>
          </Box>

          <TextField
            label="Passcode"
            value={passcode}
            onChange={handlePasscodeChange}
            disabled={!enabled || controlsDisabled}
            fullWidth
            error={enabled && !PASSCODE_PATTERN.test(passcode)}
            helperText="Required while enabled. Enter exactly 6 digits."
            inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "[0-9]*" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          autoFocus
          disabled={controlsDisabled || !canSave}
          startIcon={
            isSaving ? <CircularProgress size={16} thickness={6} /> : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
